import * as base58 from '@chainflip/utils/base58';
import { assert } from '@chainflip/utils/assertion';
import { parseUrlWithBasicAuth } from '@chainflip/utils/url';
import { BorshInstructionCoder } from '@coral-xyz/anchor';
import { PublicKey, Connection } from '@solana/web3.js';
import { bytesToHex } from '@chainflip/utils/bytes';
import { assetContractId, chainContractId } from '@chainflip/utils/chainflip';
import { POLKADOT_SS58_PREFIX } from '@chainflip/utils/consts';
import { hexEncodeNumber } from '@chainflip/utils/number';
import * as ss58 from '@chainflip/utils/ss58';
import BN from 'bn.js';
import { z } from 'zod';
import { createVaultParamsDecoder, decodeSolanaAdditionalData } from '@chainflip/scale/codecs';
import { Bytes } from 'scale-ts';

// src/address.ts
var isValidSolanaAddress = (address) => {
  try {
    const bytes = base58.decode(address);
    return bytes.length === 32;
  } catch {
    return false;
  }
};

// src/idls.ts
var mainnet = {
  address: "J88B7gmadHzTNGiy54c9Ms8BsEXNdB2fntFyhKpk3qoT",
  metadata: {
    name: "swap_endpoint",
    version: "0.1.0",
    spec: "0.1.0",
    description: "Created with Anchor"
  },
  instructions: [
    {
      name: "close_event_accounts",
      discriminator: [165, 102, 61, 1, 185, 77, 189, 121],
      accounts: [
        { name: "data_account" },
        { name: "agg_key", writable: true, signer: true },
        { name: "swap_endpoint_data_account", writable: true }
      ],
      args: []
    },
    {
      name: "fetch_swap_endpoint_native_assets",
      discriminator: [133, 121, 179, 232, 138, 188, 83, 67],
      accounts: [
        { name: "data_account" },
        {
          name: "native_vault",
          writable: true,
          pda: {
            seeds: [
              {
                kind: "const",
                value: [
                  115,
                  119,
                  97,
                  112,
                  95,
                  101,
                  110,
                  100,
                  112,
                  111,
                  105,
                  110,
                  116,
                  95,
                  110,
                  97,
                  116,
                  105,
                  118,
                  101,
                  95,
                  118,
                  97,
                  117,
                  108,
                  116
                ]
              }
            ]
          }
        },
        { name: "agg_key", writable: true, signer: true },
        { name: "system_program", address: "11111111111111111111111111111111" }
      ],
      args: [{ name: "bump", type: "u8" }]
    },
    {
      name: "initialize",
      discriminator: [175, 175, 109, 31, 13, 152, 155, 237],
      accounts: [
        {
          name: "swap_endpoint_data_account",
          writable: true,
          pda: { seeds: [{ kind: "const", value: [100, 97, 116, 97] }] }
        },
        { name: "signer", writable: true, signer: true },
        { name: "system_program", address: "11111111111111111111111111111111" }
      ],
      args: []
    },
    {
      name: "x_swap_native",
      discriminator: [163, 38, 92, 226, 243, 105, 141, 196],
      accounts: [
        { name: "data_account" },
        {
          name: "native_vault",
          writable: true,
          pda: {
            seeds: [
              {
                kind: "const",
                value: [
                  115,
                  119,
                  97,
                  112,
                  95,
                  101,
                  110,
                  100,
                  112,
                  111,
                  105,
                  110,
                  116,
                  95,
                  110,
                  97,
                  116,
                  105,
                  118,
                  101,
                  95,
                  118,
                  97,
                  117,
                  108,
                  116
                ]
              }
            ]
          }
        },
        { name: "from", writable: true, signer: true },
        { name: "event_data_account", writable: true, signer: true },
        { name: "swap_endpoint_data_account", writable: true },
        { name: "system_program", address: "11111111111111111111111111111111" }
      ],
      args: [{ name: "swap_native_params", type: { defined: { name: "SwapNativeParams" } } }]
    },
    {
      name: "x_swap_token",
      discriminator: [69, 50, 252, 99, 229, 83, 119, 235],
      accounts: [
        { name: "data_account" },
        {
          name: "token_vault_associated_token_account",
          writable: true,
          pda: {
            seeds: [
              { kind: "account", path: "data_account.token_vault_pda", account: "DataAccount" },
              {
                kind: "const",
                value: [
                  6,
                  221,
                  246,
                  225,
                  215,
                  101,
                  161,
                  147,
                  217,
                  203,
                  225,
                  70,
                  206,
                  235,
                  121,
                  172,
                  28,
                  180,
                  133,
                  237,
                  95,
                  91,
                  55,
                  145,
                  58,
                  140,
                  245,
                  133,
                  126,
                  255,
                  0,
                  169
                ]
              },
              { kind: "account", path: "mint" }
            ],
            program: {
              kind: "const",
              value: [
                140,
                151,
                37,
                143,
                78,
                36,
                137,
                241,
                187,
                61,
                16,
                41,
                20,
                142,
                13,
                131,
                11,
                90,
                19,
                153,
                218,
                255,
                16,
                132,
                4,
                142,
                123,
                216,
                219,
                233,
                248,
                89
              ]
            }
          }
        },
        { name: "from", writable: true, signer: true },
        { name: "from_token_account", writable: true },
        { name: "event_data_account", writable: true, signer: true },
        { name: "swap_endpoint_data_account", writable: true },
        { name: "token_supported_account" },
        { name: "token_program", address: "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" },
        { name: "mint" },
        { name: "system_program", address: "11111111111111111111111111111111" }
      ],
      args: [{ name: "swap_token_params", type: { defined: { name: "SwapTokenParams" } } }]
    }
  ],
  accounts: [
    { name: "DataAccount", discriminator: [85, 240, 182, 158, 76, 7, 18, 233] },
    { name: "SupportedToken", discriminator: [56, 162, 96, 99, 193, 245, 204, 108] },
    { name: "SwapEndpointDataAccount", discriminator: [79, 152, 191, 225, 128, 108, 11, 139] },
    { name: "SwapEvent", discriminator: [150, 251, 114, 94, 200, 113, 248, 70] }
  ],
  events: [
    { name: "CantDeserializeEventAccount", discriminator: [248, 26, 198, 175, 8, 58, 229, 137] },
    { name: "EventAccountNotTracked", discriminator: [202, 29, 253, 107, 20, 196, 36, 210] },
    { name: "PayeeDoesntMatchSender", discriminator: [16, 123, 0, 234, 46, 151, 175, 168] }
  ],
  errors: [
    { code: 6e3, name: "MathOverflow", msg: "Overflow in arithmetic operation" },
    { code: 6001, name: "MathUnderflow", msg: "Underflow in arithmetic operation" }
  ],
  types: [
    {
      name: "CantDeserializeEventAccount",
      type: {
        kind: "struct",
        fields: [
          { name: "event_account", type: "pubkey" },
          { name: "payee", type: "pubkey" }
        ]
      }
    },
    {
      name: "CcmParams",
      type: {
        kind: "struct",
        fields: [
          { name: "message", type: "bytes" },
          { name: "gas_amount", type: "u64" }
        ]
      }
    },
    {
      name: "DataAccount",
      docs: [
        "* ****************************************************************************\n * *************************** IMPORTANT NOTE *********************************\n * ****************************************************************************\n * If the vault is upgraded and the DataAccount struct is modified we need to\n * check the compatibility and ensure there is a proper migration process, given\n * that the Vault bytecode is the only thing being upgraded, not the data account.\n *\n * The easiest approach on upgrade is keeping the DataAccount unchanged and use\n * a new account struct for any new data that is required.\n *\n *        DO NOT MODIFY THIS WITHOUT UNDERSTANDING THE CONSEQUENCES!\n * ****************************************************************************\n * ****************************************************************************"
      ],
      type: {
        kind: "struct",
        fields: [
          { name: "agg_key", type: "pubkey" },
          { name: "gov_key", type: "pubkey" },
          { name: "token_vault_pda", type: "pubkey" },
          { name: "token_vault_bump", type: "u8" },
          { name: "upgrade_signer_pda", type: "pubkey" },
          { name: "upgrade_signer_pda_bump", type: "u8" },
          { name: "suspended", type: "bool" },
          { name: "suspended_legacy_swaps", type: "bool" },
          { name: "suspended_event_swaps", type: "bool" },
          { name: "min_native_swap_amount", type: "u64" },
          { name: "max_dst_address_len", type: "u16" },
          { name: "max_ccm_message_len", type: "u32" },
          { name: "max_cf_parameters_len", type: "u32" },
          { name: "max_event_accounts", type: "u32" }
        ]
      }
    },
    {
      name: "EventAccountNotTracked",
      type: {
        kind: "struct",
        fields: [
          { name: "event_account", type: "pubkey" },
          { name: "payee", type: "pubkey" }
        ]
      }
    },
    {
      name: "PayeeDoesntMatchSender",
      type: {
        kind: "struct",
        fields: [
          { name: "payee", type: "pubkey" },
          { name: "sender", type: "pubkey" }
        ]
      }
    },
    {
      name: "SupportedToken",
      type: {
        kind: "struct",
        fields: [
          { name: "token_mint_pubkey", type: "pubkey" },
          { name: "min_swap_amount", type: "u64" }
        ]
      }
    },
    {
      name: "SwapEndpointDataAccount",
      type: {
        kind: "struct",
        fields: [
          { name: "historical_number_event_accounts", type: "u128" },
          { name: "open_event_accounts", type: { vec: "pubkey" } }
        ]
      }
    },
    {
      name: "SwapEvent",
      type: {
        kind: "struct",
        fields: [
          { name: "creation_slot", type: "u64" },
          { name: "sender", type: "pubkey" },
          { name: "dst_chain", type: "u32" },
          { name: "dst_address", type: "bytes" },
          { name: "dst_token", type: "u32" },
          { name: "amount", type: "u64" },
          { name: "src_token", type: { option: "pubkey" } },
          { name: "ccm_parameters", type: { option: { defined: { name: "CcmParams" } } } },
          { name: "cf_parameters", type: "bytes" }
        ]
      }
    },
    {
      name: "SwapNativeParams",
      type: {
        kind: "struct",
        fields: [
          { name: "amount", type: "u64" },
          { name: "dst_chain", type: "u32" },
          { name: "dst_address", type: "bytes" },
          { name: "dst_token", type: "u32" },
          { name: "ccm_parameters", type: { option: { defined: { name: "CcmParams" } } } },
          { name: "cf_parameters", type: "bytes" }
        ]
      }
    },
    {
      name: "SwapTokenParams",
      type: {
        kind: "struct",
        fields: [
          { name: "amount", type: "u64" },
          { name: "dst_chain", type: "u32" },
          { name: "dst_address", type: "bytes" },
          { name: "dst_token", type: "u32" },
          { name: "ccm_parameters", type: { option: { defined: { name: "CcmParams" } } } },
          { name: "cf_parameters", type: "bytes" },
          { name: "decimals", type: "u8" }
        ]
      }
    }
  ]
};
var devnet = {
  address: "DeL6iGV5RWrWh7cPoEa7tRHM8XURAaB4vPjfX5qVyuWE",
  metadata: {
    name: "swap_endpoint",
    version: "0.1.0",
    spec: "0.1.0",
    description: "Created with Anchor"
  },
  instructions: [
    {
      name: "close_event_accounts",
      discriminator: [165, 102, 61, 1, 185, 77, 189, 121],
      accounts: [
        {
          name: "data_account"
        },
        { name: "agg_key", writable: true, signer: true },
        {
          name: "swap_endpoint_data_account",
          writable: true
        }
      ],
      args: []
    },
    {
      name: "fetch_swap_endpoint_native_assets",
      discriminator: [133, 121, 179, 232, 138, 188, 83, 67],
      accounts: [
        { name: "data_account" },
        {
          name: "native_vault",
          writable: true,
          pda: {
            seeds: [
              {
                kind: "const",
                value: [
                  115,
                  119,
                  97,
                  112,
                  95,
                  101,
                  110,
                  100,
                  112,
                  111,
                  105,
                  110,
                  116,
                  95,
                  110,
                  97,
                  116,
                  105,
                  118,
                  101,
                  95,
                  118,
                  97,
                  117,
                  108,
                  116
                ]
              }
            ]
          }
        },
        { name: "agg_key", writable: true, signer: true },
        {
          name: "system_program",
          address: "11111111111111111111111111111111"
        }
      ],
      args: [
        {
          name: "bump",
          type: "u8"
        }
      ]
    },
    {
      name: "initialize",
      discriminator: [175, 175, 109, 31, 13, 152, 155, 237],
      accounts: [
        {
          name: "swap_endpoint_data_account",
          writable: true,
          pda: { seeds: [{ kind: "const", value: [100, 97, 116, 97] }] }
        },
        {
          name: "signer",
          writable: true,
          signer: true
        },
        { name: "system_program", address: "11111111111111111111111111111111" }
      ],
      args: []
    },
    {
      name: "x_swap_native",
      discriminator: [163, 38, 92, 226, 243, 105, 141, 196],
      accounts: [
        {
          name: "data_account"
        },
        {
          name: "native_vault",
          writable: true,
          pda: {
            seeds: [
              {
                kind: "const",
                value: [
                  115,
                  119,
                  97,
                  112,
                  95,
                  101,
                  110,
                  100,
                  112,
                  111,
                  105,
                  110,
                  116,
                  95,
                  110,
                  97,
                  116,
                  105,
                  118,
                  101,
                  95,
                  118,
                  97,
                  117,
                  108,
                  116
                ]
              }
            ]
          }
        },
        {
          name: "from",
          writable: true,
          signer: true
        },
        { name: "event_data_account", writable: true, signer: true },
        {
          name: "swap_endpoint_data_account",
          writable: true
        },
        {
          name: "system_program",
          address: "11111111111111111111111111111111"
        }
      ],
      args: [
        {
          name: "swap_native_params",
          type: {
            defined: { name: "SwapNativeParams" }
          }
        }
      ]
    },
    {
      name: "x_swap_token",
      discriminator: [69, 50, 252, 99, 229, 83, 119, 235],
      accounts: [
        {
          name: "data_account"
        },
        {
          name: "token_vault_associated_token_account",
          writable: true,
          pda: {
            seeds: [
              {
                kind: "account",
                path: "data_account.token_vault_pda",
                account: "DataAccount"
              },
              {
                kind: "const",
                value: [
                  6,
                  221,
                  246,
                  225,
                  215,
                  101,
                  161,
                  147,
                  217,
                  203,
                  225,
                  70,
                  206,
                  235,
                  121,
                  172,
                  28,
                  180,
                  133,
                  237,
                  95,
                  91,
                  55,
                  145,
                  58,
                  140,
                  245,
                  133,
                  126,
                  255,
                  0,
                  169
                ]
              },
              {
                kind: "account",
                path: "mint"
              }
            ],
            program: {
              kind: "const",
              value: [
                140,
                151,
                37,
                143,
                78,
                36,
                137,
                241,
                187,
                61,
                16,
                41,
                20,
                142,
                13,
                131,
                11,
                90,
                19,
                153,
                218,
                255,
                16,
                132,
                4,
                142,
                123,
                216,
                219,
                233,
                248,
                89
              ]
            }
          }
        },
        {
          name: "from",
          writable: true,
          signer: true
        },
        { name: "from_token_account", writable: true },
        { name: "event_data_account", writable: true, signer: true },
        {
          name: "swap_endpoint_data_account",
          writable: true
        },
        {
          name: "token_supported_account"
        },
        { name: "token_program", address: "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" },
        { name: "mint" },
        {
          name: "system_program",
          address: "11111111111111111111111111111111"
        }
      ],
      args: [
        {
          name: "swap_token_params",
          type: {
            defined: { name: "SwapTokenParams" }
          }
        }
      ]
    }
  ],
  accounts: [
    {
      name: "DataAccount",
      discriminator: [85, 240, 182, 158, 76, 7, 18, 233]
    },
    {
      name: "SupportedToken",
      discriminator: [56, 162, 96, 99, 193, 245, 204, 108]
    },
    {
      name: "SwapEndpointDataAccount",
      discriminator: [79, 152, 191, 225, 128, 108, 11, 139]
    },
    {
      name: "SwapEvent",
      discriminator: [150, 251, 114, 94, 200, 113, 248, 70]
    }
  ],
  events: [
    {
      name: "CantDeserializeEventAccount",
      discriminator: [248, 26, 198, 175, 8, 58, 229, 137]
    },
    {
      name: "EventAccountNotTracked",
      discriminator: [202, 29, 253, 107, 20, 196, 36, 210]
    },
    {
      name: "PayeeDoesntMatchSender",
      discriminator: [16, 123, 0, 234, 46, 151, 175, 168]
    }
  ],
  errors: [
    {
      code: 6e3,
      name: "MathOverflow",
      msg: "Overflow in arithmetic operation"
    },
    { code: 6001, name: "MathUnderflow", msg: "Underflow in arithmetic operation" }
  ],
  types: [
    {
      name: "CantDeserializeEventAccount",
      type: {
        kind: "struct",
        fields: [
          { name: "event_account", type: "pubkey" },
          { name: "payee", type: "pubkey" }
        ]
      }
    },
    {
      name: "CcmParams",
      type: {
        kind: "struct",
        fields: [
          { name: "message", type: "bytes" },
          { name: "gas_amount", type: "u64" }
        ]
      }
    },
    {
      name: "DataAccount",
      docs: [
        "* ****************************************************************************\n * *************************** IMPORTANT NOTE *********************************\n * ****************************************************************************\n * If the vault is upgraded and the DataAccount struct is modified we need to\n * check the compatibility and ensure there is a proper migration process, given\n * that the Vault bytecode is the only thing being upgraded, not the data account.\n *\n * The easiest approach on upgrade is keeping the DataAccount unchanged and use\n * a new account struct for any new data that is required.\n *\n *        DO NOT MODIFY THIS WITHOUT UNDERSTANDING THE CONSEQUENCES!\n * ****************************************************************************\n * ****************************************************************************"
      ],
      type: {
        kind: "struct",
        fields: [
          {
            name: "agg_key",
            type: "pubkey"
          },
          {
            name: "gov_key",
            type: "pubkey"
          },
          {
            name: "token_vault_pda",
            type: "pubkey"
          },
          {
            name: "token_vault_bump",
            type: "u8"
          },
          {
            name: "upgrade_signer_pda",
            type: "pubkey"
          },
          {
            name: "upgrade_signer_pda_bump",
            type: "u8"
          },
          {
            name: "suspended",
            type: "bool"
          },
          {
            name: "suspended_legacy_swaps",
            type: "bool"
          },
          {
            name: "suspended_event_swaps",
            type: "bool"
          },
          {
            name: "min_native_swap_amount",
            type: "u64"
          },
          {
            name: "max_dst_address_len",
            type: "u16"
          },
          {
            name: "max_ccm_message_len",
            type: "u32"
          },
          {
            name: "max_cf_parameters_len",
            type: "u32"
          },
          {
            name: "max_event_accounts",
            type: "u32"
          }
        ]
      }
    },
    {
      name: "EventAccountNotTracked",
      type: {
        kind: "struct",
        fields: [
          {
            name: "event_account",
            type: "pubkey"
          },
          {
            name: "payee",
            type: "pubkey"
          }
        ]
      }
    },
    {
      name: "PayeeDoesntMatchSender",
      type: {
        kind: "struct",
        fields: [
          {
            name: "payee",
            type: "pubkey"
          },
          {
            name: "sender",
            type: "pubkey"
          }
        ]
      }
    },
    {
      name: "SupportedToken",
      type: {
        kind: "struct",
        fields: [
          {
            name: "token_mint_pubkey",
            type: "pubkey"
          },
          {
            name: "min_swap_amount",
            type: "u64"
          }
        ]
      }
    },
    {
      name: "SwapEndpointDataAccount",
      type: {
        kind: "struct",
        fields: [
          {
            name: "historical_number_event_accounts",
            type: "u128"
          },
          {
            name: "open_event_accounts",
            type: {
              vec: "pubkey"
            }
          }
        ]
      }
    },
    {
      name: "SwapEvent",
      type: {
        kind: "struct",
        fields: [
          {
            name: "creation_slot",
            type: "u64"
          },
          {
            name: "sender",
            type: "pubkey"
          },
          {
            name: "dst_chain",
            type: "u32"
          },
          {
            name: "dst_address",
            type: "bytes"
          },
          {
            name: "dst_token",
            type: "u32"
          },
          {
            name: "amount",
            type: "u64"
          },
          {
            name: "src_token",
            type: {
              option: "pubkey"
            }
          },
          {
            name: "ccm_parameters",
            type: {
              option: {
                defined: {
                  name: "CcmParams"
                }
              }
            }
          },
          {
            name: "cf_parameters",
            type: "bytes"
          }
        ]
      }
    },
    {
      name: "SwapNativeParams",
      type: {
        kind: "struct",
        fields: [
          {
            name: "amount",
            type: "u64"
          },
          {
            name: "dst_chain",
            type: "u32"
          },
          {
            name: "dst_address",
            type: "bytes"
          },
          {
            name: "dst_token",
            type: "u32"
          },
          {
            name: "ccm_parameters",
            type: {
              option: {
                defined: {
                  name: "CcmParams"
                }
              }
            }
          },
          {
            name: "cf_parameters",
            type: "bytes"
          }
        ]
      }
    },
    {
      name: "SwapTokenParams",
      type: {
        kind: "struct",
        fields: [
          {
            name: "amount",
            type: "u64"
          },
          {
            name: "dst_chain",
            type: "u32"
          },
          {
            name: "dst_address",
            type: "bytes"
          },
          {
            name: "dst_token",
            type: "u32"
          },
          {
            name: "ccm_parameters",
            type: {
              option: {
                defined: {
                  name: "CcmParams"
                }
              }
            }
          },
          {
            name: "cf_parameters",
            type: "bytes"
          },
          {
            name: "decimals",
            type: "u8"
          }
        ]
      }
    }
  ]
};
var tryDecodeCfParams = createVaultParamsDecoder(
  Bytes(32),
  (pubkey) => base58.encode(pubkey),
  decodeSolanaAdditionalData
);

// src/schemas.ts
var entries = Object.entries;
var bnSchema = z.any().refine((arg) => BN.isBN(arg)).transform((bn) => BigInt(bn.toString()));
var swapParams = z.object({
  amount: bnSchema,
  dst_chain: z.number(),
  dst_address: z.instanceof(Buffer),
  dst_token: z.number(),
  ccm_parameters: z.object({
    message: z.instanceof(Buffer),
    gas_amount: bnSchema
  }).nullable(),
  cf_parameters: z.instanceof(Buffer).transform((buf) => new Uint8Array(buf))
}).transform((params, ctx) => {
  const asset = entries(assetContractId).find(([, id]) => id === params.dst_token)?.[0];
  let success = true;
  if (!asset) {
    ctx.addIssue({
      message: "Failed to get destination asset",
      code: "custom",
      path: ["dst_token"]
    });
    success = false;
  }
  const chain = entries(chainContractId).find(([, id]) => id === params.dst_chain)?.[0];
  if (!chain) {
    ctx.addIssue({
      message: "Failed to get destination chain",
      code: "custom",
      path: ["dst_chain"]
    });
    success = false;
  }
  let destinationAddress;
  switch (chain) {
    case "Arbitrum":
    case "Ethereum":
      destinationAddress = `0x${params.dst_address.toString("hex")}`;
      break;
    case "Bitcoin":
      destinationAddress = params.dst_address.toString("utf8");
      break;
    case "Assethub":
    case "Polkadot":
      destinationAddress = ss58.encode({
        data: params.dst_address,
        ss58Format: POLKADOT_SS58_PREFIX
      });
      break;
    case "Solana":
      destinationAddress = base58.encode(params.dst_address);
      break;
    default: {
      assert(!success, "expected to be in error state");
    }
  }
  const decodedParams = tryDecodeCfParams(params.cf_parameters);
  if (!decodedParams.ok) {
    decodedParams.reason.forEach(
      (e) => ctx.addIssue({
        message: e.message,
        code: "custom",
        path: ["cf_parameters"]
      })
    );
    success = false;
  }
  if (!success) return z.NEVER;
  const data = decodedParams.value;
  return {
    amount: params.amount,
    destinationAddress,
    outputAsset: asset,
    affiliateFees: data.affiliateFees.map(({ account, commissionBps }) => ({
      accountIndex: account,
      commissionBps
    })),
    dcaParams: data.dcaParams && {
      chunkInterval: data.dcaParams.chunkIntervalBlocks,
      numberOfChunks: data.dcaParams.numberOfChunks
    },
    ccmDepositMetadata: params.ccm_parameters && {
      channelMetadata: {
        message: bytesToHex(params.ccm_parameters.message),
        gasBudget: hexEncodeNumber(params.ccm_parameters.gas_amount)
      },
      ccmAdditionalData: data.ccmAdditionalData
    },
    brokerFee: data.brokerFees,
    maxBoostFee: 0,
    refundParams: {
      refundAddress: data.refundParams.refundAddress,
      minPrice: BigInt(data.refundParams.minPriceX128),
      retryDuration: data.refundParams.retryDurationBlocks
    }
  };
});
var swapNativeSchema = z.object({
  name: z.literal("x_swap_native"),
  data: z.object({ swap_native_params: swapParams }).transform((d) => d.swap_native_params)
});
var swapTokenSchema = z.object({
  name: z.literal("x_swap_token"),
  data: z.object({ swap_token_params: swapParams }).transform((d) => d.swap_token_params)
});
var swapSchema = z.discriminatedUnion("name", [swapNativeSchema, swapTokenSchema]);

// src/deposit.ts
var getSolanaConnection = (solanaEndpoint) => {
  const { url, headers } = parseUrlWithBasicAuth(solanaEndpoint);
  return new Connection(url, {
    httpHeaders: headers,
    // for injecting responses in the tests
    fetch
    // fetch: async (...args) => {
    //   const res = await fetch(...args);
    //   res.text = async () => {
    //     const text = await Response.prototype.text.call(res);
    //     console.log(text);
    //     return text;
    //   };
    //   return res;
    // },
  });
};
var addresses = {
  mainnet: {
    SolUsdc: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v"
  },
  devnet: {
    SolUsdc: "4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU"
  }
};
var getSolDiff = (tx, publicKey) => {
  assert(tx.meta !== null, "tx meta is null");
  const accountIndex = tx.transaction.message.staticAccountKeys.findIndex(
    (key) => key.equals(publicKey)
  );
  const preBalance = tx.meta.preBalances.at(accountIndex);
  assert(preBalance !== void 0, "failed to find preBalance");
  const postBalance = tx.meta.postBalances.at(accountIndex);
  assert(postBalance !== void 0, "failed to find postBalance");
  const diff = postBalance - preBalance;
  return BigInt(diff);
};
var getTokenDiff = (tx, publicKey, tokenMintAddress) => {
  assert(tx.meta !== null, "tx meta is null");
  assert(tx.meta.preTokenBalances != null, "preTokenBalances is null");
  assert(tx.meta.postTokenBalances != null, "postTokenBalances is null");
  const owner = publicKey.toBase58();
  const preTokenAmount = tx.meta.preTokenBalances.find(
    (b) => b.mint === tokenMintAddress && b.owner === owner
  );
  const postTokenAmount = tx.meta.postTokenBalances.find(
    (b) => b.mint === tokenMintAddress && b.owner === owner
  );
  const preBalance = BigInt(preTokenAmount?.uiTokenAmount.amount ?? 0);
  const postBalance = BigInt(postTokenAmount?.uiTokenAmount.amount ?? 0);
  return postBalance - preBalance;
};
var TOKEN_PROGRAM_ID = new PublicKey("TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA");
var ASSOCIATED_TOKEN_PROGRAM_ID = new PublicKey(
  "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL"
);
var getAssociatedTokenAddressSync = (owner, tokenMintAddress) => {
  const [address] = PublicKey.findProgramAddressSync(
    [owner.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), new PublicKey(tokenMintAddress).toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID
  );
  return address;
};
var fetchTransfers = async (rpcUrl, depositAddressString, tokenMintAddress, depositCount) => {
  const conn = getSolanaConnection(rpcUrl);
  const depositAddress = new PublicKey(depositAddressString);
  const owner = !tokenMintAddress ? depositAddress : getAssociatedTokenAddressSync(depositAddress, tokenMintAddress);
  const results = [];
  let before;
  for (let i = 0; i < 5; i += 1) {
    const sigs = await conn.getSignaturesForAddress(owner, { limit: 20, before });
    assert(sigs.length > 0, "failed to find signatures");
    const transactions = await conn.getTransactions(
      sigs.map((s) => s.signature),
      { maxSupportedTransactionVersion: 0 }
    );
    results.push(
      ...transactions.map((tx, index) => {
        assert(tx !== null, "tx is null");
        const diff = !tokenMintAddress ? getSolDiff(tx, depositAddress) : getTokenDiff(tx, depositAddress, tokenMintAddress);
        if (diff <= 0n) return null;
        return {
          amount: diff,
          signature: sigs[index].signature,
          slot: sigs[index].slot
        };
      }).filter((transfer) => transfer !== null).sort((a, b) => b.slot - a.slot)
    );
    if (results.length >= depositCount) return results;
    before = sigs.sort((a, b) => b.slot - a.slot).at(-1)?.signature;
    assert(before, "failed to find signature for pagination");
  }
  throw new Error("too much pagination");
};
var tryToAlignDepositsAndTransfers = (transfers, deposits, offset, reportingErrorTolerance) => {
  let depIndex = 0;
  let txStartIndex = offset;
  const results = [];
  while (txStartIndex < transfers.length && depIndex < deposits.length) {
    let total = 0n;
    const currentDeposit = deposits[depIndex];
    let success = false;
    for (let txEndIndex = txStartIndex; txEndIndex < transfers.length; txEndIndex += 1) {
      if (currentDeposit.maxSlot) {
        const blockDiff = transfers[txEndIndex].slot - currentDeposit.maxSlot;
        if (blockDiff > reportingErrorTolerance) {
          throw new Error("transfers are too new");
        }
      }
      total += transfers[txEndIndex].amount;
      if (total === currentDeposit.amount) {
        const hashes = [];
        for (let i = txStartIndex; i <= txEndIndex; i += 1) {
          if (!hashes.includes(transfers[i].signature)) {
            hashes.push(transfers[i].signature);
          }
        }
        results.push(hashes);
        txStartIndex = txEndIndex + 1;
        depIndex += 1;
        success = true;
        break;
      }
    }
    if (!success) throw new Error("failed to align deposits and transfers");
  }
  assert(depIndex === deposits.length, "failed to match all deposits to transfers");
  return results;
};
var findTransactionSignatures = async (rpcUrl, depositAddress, asset, deposits, network, reportingErrorTolerance = 50) => {
  const tokenMintAddress = asset === "Sol" ? void 0 : addresses[network][asset];
  const transfers = await fetchTransfers(rpcUrl, depositAddress, tokenMintAddress, deposits.length);
  let error;
  for (let txStartIndex = 0; txStartIndex < transfers.length; txStartIndex += 1) {
    try {
      return tryToAlignDepositsAndTransfers(
        transfers,
        deposits,
        txStartIndex,
        reportingErrorTolerance
      );
    } catch (err) {
      if (err instanceof Error) error = err;
    }
  }
  throw error;
};
var findVaultSwapSignature = async (solanaEndpoint, accountAddress, slot) => {
  const connection = getSolanaConnection(solanaEndpoint);
  const allSignatures = await connection.getSignaturesForAddress(new PublicKey(accountAddress), {
    minContextSlot: slot,
    limit: 10
  });
  const signatures = allSignatures.filter((sig) => sig.slot === slot);
  assert(signatures.length === 1, "failed to find signature");
  return signatures[0].signature;
};
var findIdlAndInstruction = (tx) => {
  for (const inst of tx.transaction.message.instructions) {
    switch (inst.programId.toBase58()) {
      case mainnet.address:
        return { idl: mainnet, instruction: inst };
      case devnet.address:
        return { idl: devnet, instruction: inst };
    }
  }
  return { idl: null, instruction: null };
};
var findVaultSwapData = async (rpcUrl, signature) => {
  const methodToAsset = {
    x_swap_native: "Sol",
    x_swap_token: "SolUsdc"
  };
  const connection = getSolanaConnection(rpcUrl);
  const tx = await connection.getParsedTransaction(signature, {
    commitment: "confirmed",
    maxSupportedTransactionVersion: 0
  });
  if (!tx) return null;
  const { idl, instruction } = findIdlAndInstruction(tx);
  if (instruction === null && idl === null) return null;
  const coder = new BorshInstructionCoder(idl);
  const decoded = coder.decode(instruction.data, "base58");
  const data = swapSchema.parse(decoded);
  return {
    depositChainBlockHeight: tx.slot,
    inputAsset: methodToAsset[data.name],
    ...data.data
  };
};

export { ASSOCIATED_TOKEN_PROGRAM_ID, TOKEN_PROGRAM_ID, findTransactionSignatures, findVaultSwapData, findVaultSwapSignature, isValidSolanaAddress };
